/**
 * ConsentDialog Component - Termo de Consentimento LGPD
 * @module features/chat/components/ConsentDialog
 *
 * Componente de dialog modal para coleta de consentimento LGPD.
 * Exibe termo simplificado em português e requer escolha explícita do usuário.
 *
 * T004-FE: Componente Termo de Consentimento LGPD
 *
 * Features:
 * - Texto do termo em português (PT-BR)
 * - Botões "Concordo" (primary) e "Não concordo" (outline)
 * - Integração com useSessionStore.setConsent
 * - Acessibilidade WCAG AA compliant
 * - Não fecha com Escape ou clique no overlay (escolha obrigatória)
 */

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useSessionStore } from '../stores/sessionStore';

// =============================================================================
// TYPES
// =============================================================================

export interface ConsentDialogProps {
  /**
   * Controla se o dialog está aberto ou fechado
   */
  open: boolean;

  /**
   * Callback chamado quando o usuario aceita o termo
   */
  onAccept?: () => void;

  /**
   * Callback chamado quando o usuario recusa o termo
   */
  onDecline?: () => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Dialog de consentimento LGPD
 *
 * Exibe um termo de consentimento simplificado e coleta a resposta do usuário.
 * O consentimento é salvo no sessionStore antes de chamar os callbacks.
 *
 * @example
 * ```tsx
 * const [showConsent, setShowConsent] = useState(true);
 *
 * <ConsentDialog
 *   open={showConsent}
 *   onAccept={() => {
 *     setShowConsent(false);
 *     // Prosseguir com o fluxo
 *   }}
 *   onDecline={() => {
 *     setShowConsent(false);
 *     // Redirecionar ou mostrar mensagem
 *   }}
 * />
 * ```
 */
export function ConsentDialog({ open, onAccept, onDecline }: ConsentDialogProps) {
  const setConsent = useSessionStore((state) => state.setConsent);

  /**
   * Handler para aceitação do termo
   * Salva consentimento no store e chama callback
   */
  const handleAccept = () => {
    setConsent(true);
    onAccept?.();
  };

  /**
   * Handler para recusa do termo
   * Salva negativa no store e chama callback
   */
  const handleDecline = () => {
    setConsent(false);
    onDecline?.();
  };

  /**
   * Previne fechamento por interação com overlay
   * O usuário deve fazer uma escolha explícita
   */
  const handleInteractOutside = (event: Event) => {
    event.preventDefault();
  };

  /**
   * Previne fechamento por tecla Escape
   * O usuário deve fazer uma escolha explícita
   */
  const handleEscapeKeyDown = (event: KeyboardEvent) => {
    event.preventDefault();
  };

  return (
    <Dialog open={open} modal>
      <DialogContent
        hideCloseButton
        onInteractOutside={handleInteractOutside}
        onEscapeKeyDown={handleEscapeKeyDown}
      >
        <DialogHeader>
          <DialogTitle>Termo de Consentimento - LGPD</DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-4 text-sm text-muted-foreground">
              <p>
                Para realizar o diagnóstico, coletamos alguns dados pessoais com a finalidade de
                personalizar sua experiência e gerar seu relatório.
              </p>

              <div className="space-y-2">
                <p className="font-medium text-foreground">Dados coletados:</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Nome completo</li>
                  <li>Email profissional</li>
                  <li>Empresa (opcional)</li>
                  <li>Cargo (opcional)</li>
                  <li>Respostas do review</li>
                </ul>
              </div>

              <div className="space-y-2">
                <p className="font-medium text-foreground">Objetivo e compartilhamento:</p>
                <p>
                  Seus dados serão utilizados para gerar o diagnóstico personalizado e podem ser
                  compartilhados com terceiros (HubSpot) para fins de contato comercial, conforme
                  previsto na Lei Geral de Proteção de Dados (LGPD).
                </p>
              </div>

              <div className="space-y-2">
                <p className="font-medium text-foreground">Seus direitos:</p>
                <p>
                  Você pode solicitar acesso, correção ou exclusão dos seus dados a qualquer momento
                  através do email: <span className="text-primary">contato@bossabox.com</span>
                </p>
              </div>

              <p className="text-xs text-muted-foreground/80">
                Ao clicar em "Concordo", você autoriza a coleta e o tratamento dos seus dados
                conforme descrito acima.
              </p>
            </div>
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={handleDecline}
            className="w-full sm:w-auto"
            aria-label="Não concordo com o termo de consentimento"
          >
            Não concordo
          </Button>
          <Button
            onClick={handleAccept}
            className="w-full sm:w-auto"
            aria-label="Concordo com o termo de consentimento"
          >
            Concordo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ConsentDialog;
